// Teacher-only, read-only content contract; never creates a student round.
export const TRAIN_AUDIO_MANIFEST = 'classroom-balloon-verified-v2';
const alphabet = new Set([...'abcçdefgğhıijklmnoöprsştuüvyz']);
export const trainText = text => typeof text === 'string' ? text.normalize('NFC').toLocaleLowerCase('tr-TR').trim() : '';
export function shuffleTrain(items, random = Math.random) {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.min(i, Math.floor(random() * (i + 1)));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}
export function trainPool(data) {
  if (data?.audio_manifest !== TRAIN_AUDIO_MANIFEST) return [];
  const learned = new Set((data.learned_letters || []).map(trainText));
  const unique = new Map();
  for (const entry of data.entries || []) {
    const text = trainText(entry.text), letters = [...text];
    if (!['word', 'syllable'].includes(entry.kind) || letters.length < 2
      || !letters.every(letter => alphabet.has(letter) && learned.has(letter))
      || !entry.audio?.url || !/^[a-f0-9]{64}$/i.test(entry.audio?.sha256 || '')) continue;
    if (!unique.has(text)) unique.set(text, { ...entry, text });
  }
  return [...unique.values()];
}
export function nextTrainSession(session, data, random = Math.random) {
  const pool = trainPool(data), byText = new Map(pool.map(entry => [entry.text, entry]));
  if (!pool.length) return null;
  let used = new Set(session ? [...session.used, session.target.text] : []);
  let bag = (session?.bag || []).filter(text => byText.has(text) && !used.has(text));
  bag.push(...shuffleTrain(pool.filter(entry => !used.has(entry.text) && !bag.includes(entry.text)).map(entry => entry.text), random));
  if (!bag.length) {
    used = new Set(); bag = shuffleTrain(pool.map(entry => entry.text), random);
    // Keep consecutive targets different at the boundary whenever possible.
    if (bag.length > 1 && bag[0] === session?.target.text) [bag[0], bag[1]] = [bag[1], bag[0]];
  }
  const target = byText.get(bag.shift());
  return { target, bag, used: [...used] };
}
export function trainAttempt(session, random = Math.random) {
  const tiles = session ? shuffleTrain([...session.target.text].map((letter, index) => ({ id: `tile-${index}`, letter })), random) : [];
  // Without distractors, a naturally correct shuffle would reveal the answer.
  // Ensure a different order whenever the word has at least two distinct letters.
  if (session && tiles.map(tile => tile.letter).join('') === session.target.text) {
    const different = tiles.findIndex(tile => tile.letter !== tiles[0].letter);
    if (different > 0) [tiles[0], tiles[different]] = [tiles[different], tiles[0]];
  }
  return { session, tiles, slots: session ? Array([...session.target.text].length).fill(null) : [],
    phase: session ? 'speaking' : 'empty' };
}
export function trainReducer(state, action) {
  if (action.type === 'phase') return { ...state, phase: action.phase };
  if (action.type === 'reset') return trainAttempt(action.session);
  if (action.type === 'wrong-reset') return { ...trainAttempt(state.session), phase: 'speaking' };
  if (state.phase !== 'ready') return state;
  if (action.type === 'clear') return { ...state, slots: state.slots.map((id, i) => i === action.index ? null : id) };
  if (action.type !== 'place' || !state.tiles.some(tile => tile.id === action.id) || state.slots.includes(action.id)) return state;
  const index = action.index ?? state.slots.indexOf(null);
  if (index < 0 || index >= state.slots.length) return state;
  // A drop onto a filled wagon returns its previous tile to the source tray.
  const slots = state.slots.map((id, i) => i === index ? action.id : id);
  const answer = slots.map(id => state.tiles.find(tile => tile.id === id)?.letter || '').join('');
  const phase = slots.every(Boolean) ? (answer === state.session.target.text ? 'correct' : 'wrong') : 'ready';
  return { ...state, slots, phase };
}
